import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Descriptions,
  Drawer,
  Flex,
  Input,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  EyeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  RazorpayOrder,
  RazorpayPaymentData,
  RazorpayPaymentsResponse,
  useGetRazorpayOrderPaymentsQuery,
  useGetRazorpayOrdersQuery,
} from "../../../api/webshop/rzp/RazorpayOrdersApi";

const ZERO_DECIMAL_CURRENCIES = new Set(["JPY", "KRW", "VND"]);

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function parseNotes(notes: unknown): Record<string, unknown> | undefined {
  if (typeof notes === "string") {
    try {
      return asRecord(JSON.parse(notes));
    } catch {
      return undefined;
    }
  }
  return asRecord(notes);
}

function formatDate(value?: string | number) {
  if (!value) return "-";

  const date = typeof value === "number"
    ? new Date(value * 1000)
    : new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`);

  if (Number.isNaN(date.getTime())) return String(value);

  return `${date.toLocaleString("en-SG", {
    timeZone: "Asia/Singapore",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })} UTC+8`;
}

function getOrderDate(record: RazorpayOrder) {
  return record.OrderDt
    ?? record.OrderDetails?.OrderDt
    ?? record.OrderDetails?.rzp_payment_process_response?.created_at
    ?? record.OrderDetails?.rzp_create_order_response?.created_at;
}

function formatAmount(amount?: number, currency = "SGD") {
  if (amount === undefined || amount === null) return "-";

  const divisor = ZERO_DECIMAL_CURRENCIES.has(currency) ? 1 : 100;
  return new Intl.NumberFormat("en-SG", {
    style: "currency",
    currency,
    maximumFractionDigits: ZERO_DECIMAL_CURRENCIES.has(currency) ? 0 : 2,
  }).format(amount / divisor);
}

function getPayment(record: RazorpayOrder): RazorpayPaymentData | undefined {
  return record.OrderDetails?.rzp_payment_process_response
    ?? record.OrderDetails?.rzp_create_order_response;
}

function getRazorpayOrderNumber(record: RazorpayOrder) {
  return record.OrderDetails?.rzp_create_order_response?.id
    ?? record.OrderDetails?.rzp_payment_process_response?.id
    ?? record.OrderId;
}

function asPaymentsResponse(value: unknown): RazorpayPaymentsResponse | undefined {
  const record = asRecord(value);
  if (!record) return undefined;

  return {
    ...record,
    count: typeof record.count === "number" ? record.count : undefined,
    items: Array.isArray(record.items)
      ? record.items.filter((item): item is RazorpayPaymentData => Boolean(asRecord(item)))
      : undefined,
  };
}

function getAttachedPayments(record: RazorpayOrder): RazorpayPaymentData[] {
  const details = record.OrderDetails;
  const candidates = [
    record.RazorpayPayments,
    details?.rzp_payments_response,
    details?.payments,
    details?.rzp_payments,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate.filter((item): item is RazorpayPaymentData => Boolean(asRecord(item)));
    }

    const response = asPaymentsResponse(candidate);
    if (response?.items?.length) {
      return response.items;
    }
  }

  return [];
}

function getCurrency(record: RazorpayOrder) {
  return getPayment(record)?.currency
    ?? record.OrderDetails?.currency
    ?? "SGD";
}

function getAmount(record: RazorpayOrder) {
  const payment = getPayment(record);
  return payment?.amount_paid
    ?? payment?.amount
    ?? record.OrderDetails?.rzp_create_order_response?.amount;
}

function formatPaymentLabel(payment: RazorpayPaymentData) {
  const parts = [
    payment.id,
    payment.status,
    payment.amount !== undefined
      ? formatAmount(payment.amount, payment.currency ?? "SGD")
      : undefined,
  ].filter(Boolean);

  return parts.join(" / ") || "-";
}

function getEmail(record: RazorpayOrder) {
  const detailEmail = record.OrderDetails?.email;
  if (detailEmail) return detailEmail;

  const createNotes = parseNotes(record.OrderDetails?.rzp_create_order_response?.notes);
  const processNotes = parseNotes(record.OrderDetails?.rzp_payment_process_response?.notes);
  return String(createNotes?.email ?? processNotes?.email ?? "-");
}

function getSkuSummary(record: RazorpayOrder) {
  const skus = record.OrderDetails?.skus;
  if (!skus || Object.keys(skus).length === 0) return "-";

  return Object.entries(skus)
    .map(([sku, qty]) => `${sku} x${qty}`)
    .join(", ");
}

function getSkuCount(record: RazorpayOrder) {
  const skus = record.OrderDetails?.skus;
  if (!skus) return 0;

  return Object.values(skus).reduce((total, qty) => total + Number(qty || 0), 0);
}

function getLoyaltyPointsAwarded(record: RazorpayOrder) {
  if (typeof record.LoyaltyPointsAwarded === "number") {
    return record.LoyaltyPointsAwarded;
  }

  return (record.LoyaltyPointAwards ?? []).reduce(
    (total, award) => total + Number(award.PointsDelta || 0),
    0,
  );
}

function getLoyaltyPointAccounts(record: RazorpayOrder) {
  if (record.LoyaltyPointAccountDetails?.length) {
    return record.LoyaltyPointAccountDetails.map((account) => ({
      userId: account.UserId,
      email: account.Email,
    }));
  }

  if (record.LoyaltyPointAccounts?.length) {
    return record.LoyaltyPointAccounts.map((userId) => ({
      userId,
      email: undefined,
    }));
  }

  return Array.from(new Set(
    (record.LoyaltyPointAwards ?? [])
      .map((award) => award.UserId)
      .filter((account): account is string => Boolean(account)),
  )).map((userId) => ({ userId, email: undefined }));
}

function formatAccountLabel(account: string) {
  if (account.length <= 16) return account;
  return `${account.slice(0, 8)}...${account.slice(-6)}`;
}

function renderLoyaltyAward(record: RazorpayOrder) {
  const points = getLoyaltyPointsAwarded(record);
  const accounts = getLoyaltyPointAccounts(record);

  if (!points && accounts.length === 0) {
    return <Typography.Text type="secondary">
      Not awarded (not logged in)
    </Typography.Text>;
  }

  return (
    <Space direction="vertical" size={0}>
      <Typography.Text strong>{points.toLocaleString()} pts</Typography.Text>
      {accounts.length > 0 ? (
        accounts.map((account) => (
          <Typography.Text
            key={account.userId ?? account.email ?? "unknown"}
            copyable={{ text: account.email ?? account.userId ?? "" }}
            style={{ maxWidth: 170 }}
            type="secondary"
            ellipsis
          >
            {account.email ?? formatAccountLabel(account.userId ?? "")}
          </Typography.Text>
        ))
      ) : (
        <Typography.Text type="secondary">No account</Typography.Text>
      )}
    </Space>
  );
}

function getStatusColor(status?: string) {
  switch (status?.toLowerCase()) {
    case "success":
    case "paid":
    case "captured":
      return "green";
    case "pending":
    case "created":
    case "attempted":
      return "gold";
    case "failed":
    case "cancelled":
      return "red";
    default:
      return "default";
  }
}

function renderJson(value: unknown) {
  return (
    <pre
      style={{
        background: "#f5f5f5",
        borderRadius: 6,
        margin: 0,
        maxHeight: 320,
        overflow: "auto",
        padding: 12,
        whiteSpace: "pre-wrap",
      }}
    >
      {JSON.stringify(value ?? {}, null, 2)}
    </pre>
  );
}

export default function RzpViewOrders() {
  const { data, error, isFetching, refetch } = useGetRazorpayOrdersQuery();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<RazorpayOrder | undefined>();

  const orders = useMemo(() => {
    const source = data?.Orders ?? [];
    const normalizedQuery = searchQuery.trim().toLowerCase();

    const sorted = [...source].sort((a, b) => {
      const aDate = new Date(String(getOrderDate(a) ?? "")).getTime() || 0;
      const bDate = new Date(String(getOrderDate(b) ?? "")).getTime() || 0;
      return bDate - aDate;
    });

    if (!normalizedQuery) return sorted;

    return sorted.filter((order) => {
      const haystack = [
        order.OrderId,
        getRazorpayOrderNumber(order),
        order.Status,
        order.MarketplacePlatform,
        order.SimplyWsUserSub,
        getEmail(order),
        getSkuSummary(order),
        String(getLoyaltyPointsAwarded(order)),
        ...getAttachedPayments(order).flatMap((payment) => [
          payment.id,
          payment.status,
          payment.email,
          payment.contact,
        ]),
        ...getLoyaltyPointAccounts(order).flatMap((account) => [
          account.email,
          account.userId,
        ]),
        order.OrderDetails?.promoData?.promoCode,
        JSON.stringify(order.OrderDetails ?? {}),
      ].join(" ").toLowerCase();

      return haystack.includes(normalizedQuery);
    });
  }, [data, searchQuery]);

  const columns: ColumnsType<RazorpayOrder> = [
    {
      title: "Order",
      dataIndex: "OrderId",
      key: "OrderId",
      fixed: "left",
      width: 210,
      render: (value: string) => (
        <Typography.Text copyable={{ text: value }} style={{ maxWidth: 180 }} ellipsis>
          {value}
        </Typography.Text>
      ),
    },
    {
      title: "Razorpay Order",
      key: "razorpayOrder",
      width: 210,
      render: (_, record) => {
        const orderNumber = getRazorpayOrderNumber(record);
        return (
          <Typography.Text copyable={{ text: orderNumber }} style={{ maxWidth: 180 }} ellipsis>
            {orderNumber}
          </Typography.Text>
        );
      },
    },
    {
      title: "Date",
      key: "date",
      width: 190,
      render: (_, record) => formatDate(getOrderDate(record)),
      sorter: (a, b) => {
        const aDate = new Date(String(getOrderDate(a) ?? "")).getTime() || 0;
        const bDate = new Date(String(getOrderDate(b) ?? "")).getTime() || 0;
        return aDate - bDate;
      },
      defaultSortOrder: "descend",
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      width: 120,
      render: (status?: string) => (
        <Tag color={getStatusColor(status)}>{status ?? "UNKNOWN"}</Tag>
      ),
      filters: [
        { text: "Pending", value: "PENDING" },
        { text: "Success", value: "SUCCESS" },
        { text: "Failed", value: "FAILED" },
      ],
      onFilter: (value, record) => record.Status === value,
    },
    {
      title: "Razorpay Status",
      key: "rzpStatus",
      width: 150,
      render: (_, record) => {
        const status = getPayment(record)?.status;
        return <Tag color={getStatusColor(status)}>{status ?? "-"}</Tag>;
      },
    },
    {
      title: "Payments",
      key: "payments",
      width: 260,
      render: (_, record) => {
        const payments = getAttachedPayments(record);

        if (payments.length === 0) {
          return <Typography.Text type="secondary">Open to load</Typography.Text>;
        }

        return (
          <Space direction="vertical" size={0}>
            {payments.map((payment, index) => (
              <Typography.Text
                key={payment.id ?? index}
                copyable={payment.id ? { text: payment.id } : false}
                style={{ maxWidth: 230 }}
                ellipsis
              >
                {formatPaymentLabel(payment)}
              </Typography.Text>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Amount",
      key: "amount",
      width: 140,
      align: "right",
      render: (_, record) => formatAmount(getAmount(record), getCurrency(record)),
    },
    {
      title: "Email",
      key: "email",
      width: 230,
      ellipsis: true,
      render: (_, record) => getEmail(record),
    },
    {
      title: "Items",
      key: "items",
      width: 90,
      align: "right",
      render: (_, record) => getSkuCount(record),
    },
    {
      title: "Promo",
      key: "promo",
      width: 130,
      render: (_, record) => record.OrderDetails?.promoData?.promoCode ?? "-",
    },
    {
      title: "Points Awarded",
      key: "pointsAwarded",
      width: 210,
      render: (_, record) => renderLoyaltyAward(record),
      sorter: (a, b) => getLoyaltyPointsAwarded(a) - getLoyaltyPointsAwarded(b),
    },
    {
      title: "",
      key: "actions",
      width: 64,
      align: "center",
      render: (_, record) => (
        <Tooltip title="View order">
          <Button
            icon={<EyeOutlined />}
            onClick={() => setSelectedOrder(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Flex gap={12} vertical>
      <Flex justify="space-between" align="end" wrap="wrap" gap={12}>
        <div>
          <Typography.Title level={2} style={{ margin: 0 }}>
            Razorpay Orders
          </Typography.Title>
          <Typography.Text type="secondary">
            {orders.length} of {data?.Count ?? 0} order{(data?.Count ?? 0) === 1 ? "" : "s"}
          </Typography.Text>
        </div>
        <Space>
          <Input.Search
            allowClear
            placeholder="Search orders, email, SKU, promo"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            style={{ width: 320 }}
          />
          <Tooltip title="Reload">
            <Button icon={<ReloadOutlined />} onClick={refetch} />
          </Tooltip>
        </Space>
      </Flex>

      {error && (
        <Alert
          message="Unable to load Razorpay orders"
          description="The orders endpoint did not return data. Try reloading, or check that the API route has been deployed for this environment."
          type="error"
          showIcon
          action={<Button onClick={refetch}>Try Again</Button>}
        />
      )}

      <Table
        rowKey={(record) => `${record.OrderId}-${record.ShopId}`}
        dataSource={orders}
        loading={isFetching}
        columns={columns}
        scroll={{ x: 1500 }}
      />

      <OrderDetailsDrawer
        order={selectedOrder}
        onClose={() => setSelectedOrder(undefined)}
      />
    </Flex>
  );
}

function OrderDetailsDrawer(props: {
  order?: RazorpayOrder;
  onClose: () => void;
}) {
  const { order, onClose } = props;
  const {
    data: paymentDetails,
    error: paymentError,
    isFetching: isFetchingPayments,
    refetch: refetchPayments,
  } = useGetRazorpayOrderPaymentsQuery(order?.OrderId ?? "", {
    skip: !order?.OrderId,
  });
  const details = order?.OrderDetails;
  const paymentSource = order && paymentDetails?.RazorpayPayments
    ? {
        ...order,
        RazorpayPayments: paymentDetails.RazorpayPayments,
        OrderDetails: {
          ...details,
          rzp_payments_response: paymentDetails.RazorpayPayments,
        },
      }
    : order;
  const createResponse = details?.rzp_create_order_response;
  const processResponse = details?.rzp_payment_process_response;
  const attachedPayments = paymentSource ? getAttachedPayments(paymentSource) : [];
  const skuRows = Object.entries(details?.skus ?? {}).map(([sku, qty]) => ({
    sku,
    qty,
  }));
  const loyaltyAwardRows = order?.LoyaltyPointAwards ?? [];
  const loyaltyAccounts = order ? getLoyaltyPointAccounts(order) : [];

  return (
    <Drawer
      title="Razorpay Order Details"
      open={Boolean(order)}
      onClose={onClose}
      width={760}
    >
      {order && (
        <Flex gap={16} vertical>
          <Descriptions
            bordered
            column={1}
            size="small"
            items={[
              {
                key: "orderId",
                label: "Order ID",
                children: <Typography.Text copyable>{order.OrderId}</Typography.Text>,
              },
              {
                key: "status",
                label: "Status",
                children: <Tag color={getStatusColor(order.Status)}>{order.Status ?? "UNKNOWN"}</Tag>,
              },
              {
                key: "rzpStatus",
                label: "Razorpay Status",
                children: (
                  <Tag color={getStatusColor(getPayment(order)?.status)}>
                    {getPayment(order)?.status ?? "-"}
                  </Tag>
                ),
              },
              {
                key: "rzpOrder",
                label: "Razorpay Order Number",
                children: (
                  <Typography.Text copyable>
                    {getRazorpayOrderNumber(order)}
                  </Typography.Text>
                ),
              },
              {
                key: "date",
                label: "Order Date",
                children: formatDate(getOrderDate(order)),
              },
              {
                key: "amount",
                label: "Amount",
                children: formatAmount(getAmount(order), getCurrency(order)),
              },
              {
                key: "email",
                label: "Email",
                children: getEmail(order),
              },
              {
                key: "sub",
                label: "User ID",
                children: order.SimplyWsUserSub ?? details?.sub ?? "-",
              },
              {
                key: "pointsAwarded",
                label: "Points Awarded",
                children: `${getLoyaltyPointsAwarded(order).toLocaleString()} pts`,
              },
              {
                key: "pointsAccount",
                label: "Awarded To",
                children: loyaltyAccounts.length > 0 ? (
                  <Space direction="vertical" size={0}>
                    {loyaltyAccounts.map((account) => (
                      <Typography.Text
                        key={account.userId ?? account.email ?? "unknown"}
                        copyable={{ text: account.email ?? account.userId ?? "" }}
                      >
                        {account.email ?? account.userId}
                      </Typography.Text>
                    ))}
                  </Space>
                ) : "-",
              },
              {
                key: "platform",
                label: "Platform",
                children: order.MarketplacePlatform ?? "-",
              },
            ]}
          />

          <div>
            <Typography.Title level={4}>Items</Typography.Title>
            <Table
              rowKey="sku"
              dataSource={skuRows}
              pagination={false}
              size="small"
              columns={[
                {
                  title: "SKU",
                  dataIndex: "sku",
                  key: "sku",
                },
                {
                  title: "Quantity",
                  dataIndex: "qty",
                  key: "qty",
                  align: "right",
                  width: 100,
                },
              ]}
            />
          </div>

          {details?.promoData && (
            <Descriptions
              bordered
              column={1}
              size="small"
              title="Promotion"
              items={[
                {
                  key: "code",
                  label: "Code",
                  children: details.promoData.promoCode ?? "-",
                },
                {
                  key: "discount",
                  label: "Discount",
                  children: details.promoData.discountAmount ?? "-",
                },
                {
                  key: "type",
                  label: "Type",
                  children: details.promoData.discountType ?? "-",
                },
              ]}
            />
          )}

          <div>
            <Typography.Title level={4}>Razorpay Payments</Typography.Title>
            {paymentError && (
              <Alert
                message="Unable to load Razorpay payments"
                description="Showing cached payment details if available."
                type="warning"
                showIcon
                action={<Button onClick={refetchPayments}>Retry</Button>}
                style={{ marginBottom: 12 }}
              />
            )}
            <Table
              rowKey={(payment, index) => payment.id ?? String(index)}
              dataSource={attachedPayments}
              loading={isFetchingPayments}
              pagination={false}
              size="small"
              locale={{ emptyText: "No payments attached to this order" }}
              columns={[
                {
                  title: "Payment ID",
                  dataIndex: "id",
                  key: "id",
                  render: (value?: string) => value ? (
                    <Typography.Text copyable>{value}</Typography.Text>
                  ) : "-",
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  width: 120,
                  render: (status?: string) => (
                    <Tag color={getStatusColor(status)}>{status ?? "-"}</Tag>
                  ),
                },
                {
                  title: "Amount",
                  key: "amount",
                  align: "right",
                  width: 130,
                  render: (_, payment) => formatAmount(payment.amount, payment.currency ?? getCurrency(order)),
                },
                {
                  title: "Email",
                  dataIndex: "email",
                  key: "email",
                  render: (value?: string) => value ?? "-",
                },
                {
                  title: "Created",
                  dataIndex: "created_at",
                  key: "created_at",
                  width: 170,
                  render: (value?: number) => formatDate(value),
                },
              ]}
            />
          </div>

          <Collapse
            items={[
              {
                key: "create",
                label: "Razorpay create order response",
                children: renderJson(createResponse),
              },
              {
                key: "process",
                label: "Razorpay payment process response",
                children: renderJson(processResponse),
              },
              {
                key: "payments",
                label: "Razorpay payments response",
                children: renderJson(
                  paymentDetails?.RazorpayPayments
                    ?? details?.rzp_payments_response
                    ?? order.RazorpayPayments,
                ),
              },
              {
                key: "loyalty",
                label: "Loyalty point awards",
                children: renderJson(loyaltyAwardRows),
              },
              {
                key: "raw",
                label: "Raw order record",
                children: renderJson(order),
              },
            ]}
          />
        </Flex>
      )}
    </Drawer>
  );
}
